import { useMemo, useState } from 'react';
import {
    AvailabilityBlock,
    AvailabilityBlockState,
    AvailabilityBlockUpdateParameters,
    Reservation,
} from '@mews-commander/data-access';
import { logSentryError, lt, showErrorAlert, showSuccessAlert } from '@mews/framework';
import { isCanceledState } from '@mews-commander/types';
import { updateAvailabilityBlocks } from '@mews-commander/legacy-entities/src/actions/availabilityBlocks';
import { isReservationCanceled, compareEndUtcDesc } from '@mews-commander/legacy-entities/src/utils/reservations';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import CancelAvailabilityBlockDialog, { CancelFormValues } from '../../Dialogs/CancelAvailabilityBlockDialog';
import { ChangeAvailabilityBlockStateBlockedDialog } from '../../Dialogs/ChangeAvailabilityBlockStateBlockedDialog';
import { getAvailabilityBlockStateChangedSuccessAlert } from './availabilityBlockStates';
import { useAvailabilityBlockReservations } from './useAvailabilityBlockReservations';

type UpdateBlockStateValues = Omit<AvailabilityBlockUpdateParameters, 'availabilityBlockId' | 'state'> & {
    state: { value: AvailabilityBlockState };
};

interface AvailabilityBlockStateActions {
    makeInquired: () => void;
    makeOptional: () => void;
    confirm: () => void;
    cancel: () => void;
    dialog: React.ReactNode;
}

export const useAvailabilityBlockStateActions = (
    availabilityBlock: AvailabilityBlock
): AvailabilityBlockStateActions => {
    const dispatch = useCommanderThunkDispatch();

    const [dialog, setDialog] = useState<'cancel' | 'reservations' | null>(null);

    const { cancellableReservations, nonCancellableReservations, allReservations } = useAvailabilityBlockReservations(
        availabilityBlock.id
    );

    const nonCancelableReservationsWithoutCanceled = nonCancellableReservations.filter(
        (r) => !isCanceledState(r.serviceOrderState)
    );

    const isPossibleToChangeState = () => {
        if (nonCancelableReservationsWithoutCanceled.length) {
            showErrorAlert({
                title: lt('AvailabilityBlockStateChangeForbiddenTitle'),
                description: lt('AvailabilityBlockStateChangeForbiddenDesc'),
            });
            return false;
        }

        if (cancellableReservations.length) {
            setDialog('reservations');
            return false;
        }

        return true;
    };

    const getBlockStateUpdateFunction = (values: UpdateBlockStateValues) => async () => {
        if (!isPossibleToChangeState()) {
            return;
        }

        try {
            await dispatch(
                updateAvailabilityBlocks({
                    availabilityBlocks: [{ availabilityBlockId: availabilityBlock.id, ...values }],
                })
            );
            showSuccessAlert(getAvailabilityBlockStateChangedSuccessAlert(values.state.value));
        } catch (error) {
            dispatch(logSentryError({ error }));
        }
    };

    const cancel = async (values: CancelFormValues) => {
        const resp = await getBlockStateUpdateFunction({
            state: { value: AvailabilityBlockState.Canceled },
            cancellationReason: { value: values.cancellationReason },
            cancellationReasonDetail: { value: values.cancellationReasonDetail },
        })();
        closeDialog();
        return resp;
    };

    const closeDialog = () => {
        setDialog(null);
    };

    const dialogElement = getDialog({
        dialog,
        cancel,
        closeDialog,
        allReservations,
        availabilityBlockId: availabilityBlock.id,
        cancellableReservationsCount: cancellableReservations.length,
    });

    return useMemo(
        () => ({
            dialog: dialogElement,
            makeInquired: getBlockStateUpdateFunction({ state: { value: AvailabilityBlockState.Inquired } }),
            makeOptional: getBlockStateUpdateFunction({ state: { value: AvailabilityBlockState.Optional } }),
            confirm: getBlockStateUpdateFunction({ state: { value: AvailabilityBlockState.Confirmed } }),
            cancel: () => {
                if (isPossibleToChangeState()) {
                    setDialog('cancel');
                }
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [availabilityBlock, dialog]
    );
};

const getDialog = ({
    dialog,
    allReservations,
    cancel,
    closeDialog,
    cancellableReservationsCount,
    availabilityBlockId,
}: {
    dialog: 'reservations' | 'cancel' | null;
    allReservations: Reservation[];
    cancel: (values: CancelFormValues) => Promise<unknown>;
    closeDialog: () => void;
    cancellableReservationsCount: number;
    availabilityBlockId: string;
}) => {
    if (dialog === 'cancel') {
        const lastCancelledReservation = allReservations.sort(compareEndUtcDesc).find(isReservationCanceled);
        return (
            <CancelAvailabilityBlockDialog
                onSubmit={cancel}
                onClose={closeDialog}
                initialValues={
                    lastCancelledReservation && {
                        cancellationReason: lastCancelledReservation.cancellationReason ?? undefined,
                        cancellationReasonDetail: lastCancelledReservation.cancellationReasonDetail,
                    }
                }
            />
        );
    }

    if (dialog === 'reservations' && cancellableReservationsCount) {
        return (
            <ChangeAvailabilityBlockStateBlockedDialog
                onClose={closeDialog}
                reservationsCount={cancellableReservationsCount}
                availabilityBlockId={availabilityBlockId}
            />
        );
    }

    return null;
};
